import { createDemoChannel } from './realtime';
import { isDemoSession } from './session';

/**
 * The second seam.
 *
 * Patches `channel`/`removeChannel` on the client instance rather than
 * guarding each call site, because three of the eight `.channel()` calls live
 * in `packages/hooks` and CLAUDE.md forbids web-only imports there. One patch
 * reaches all eight and cannot be applied partially.
 *
 * Statically imported, unlike the fetch layer: `client.channel()` must return
 * a channel synchronously, so there is no await to hide a dynamic import
 * behind. That is why `realtime.ts` depends only on `filters.ts` — the engine,
 * store and generator stay lazy behind the fetch shim.
 */
/**
 * Typed loosely on purpose. supabase-js's `channel`/`removeChannel` signatures
 * are generic over its own RealtimeChannel, and matching them exactly would
 * mean importing and satisfying that whole type for a fake we only need to
 * behave, not typecheck, like the real one at the call sites.
 */
type PatchableClient = {
  channel: (name: string, opts?: never) => unknown;
  removeChannel: (channel: never) => unknown;
};

export function installDemoRealtime(client: unknown): void {
  const target = client as PatchableClient;
  const realChannel = target.channel.bind(target);
  const realRemove = target.removeChannel.bind(target);

  target.channel = (name: string, opts?: never) =>
    isDemoSession() ? createDemoChannel(name) : realChannel(name, opts);

  target.removeChannel = (channel: never) => {
    const maybe = channel as { unsubscribe?: () => Promise<'ok'>; topic?: unknown };
    // A demo channel is ours to close; anything else goes to the real client,
    // including a real channel opened before the demo session started.
    if (typeof maybe?.unsubscribe === 'function' && typeof maybe.topic === 'string' && isDemoSession()) {
      return maybe.unsubscribe();
    }
    return realRemove(channel);
  };
}
